import { createContext, ReactNode, useContext, useEffect, useState } from "react";

type ThemeType = 'dark' | 'light' | 'custom';

interface IThemeContext {
    theme: ThemeType;
    toggleTheme: (newTheme?: ThemeType) => void;
    UpdateCustomThemeColor: (colorName: string, colorValue: string) => void;
    UpdateCustomThemeImage: (imageName: string, imageValue: string) => void;
    getCustomThemeImage: (imageName: string) => string;
    resetCustomTheme: () => void;
}

const ThemeContext = createContext<IThemeContext | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const useTheme = (): IThemeContext => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme sha de utilitzar dins un ThemeProvider');
    }
    return context;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setTheme] = useState<ThemeType>(() => {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'custom') return 'custom';
        return (savedTheme === 'dark' ? 'dark' : 'light');
    })

    // Estat per mantenir imatges en memòria per rendiment
    const [themeImages, setThemeImages] = useState<Record<string, string>>({});

    const normalizeThemeImageSrc = (src: string): string => {
        const value = src?.trim();
        if (!value) return '';

        // Keep fully qualified/data/blob URLs untouched.
        if (/^(data:|blob:|https?:\/\/|\/\/)/i.test(value)) {
            return value;
        }

        const publicUrl = process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '.'
            ? process.env.PUBLIC_URL.replace(/\/$/, '')
            : '';

        if (value.startsWith('/')) {
            if (publicUrl && !value.startsWith(`${publicUrl}/`)) {
                return `${publicUrl}${value}`;
            }
            return value;
        }

        try {
            const base = `${window.location.origin}${publicUrl}/`;
            return new URL(value, base).toString();
        } catch {
            return value;
        }
    };

    const toggleTheme = (newTheme?: ThemeType): void => {
        setTheme(prevTheme => {
            let nextTheme: ThemeType;

            if (newTheme) {
                // Si se proporciona un tema específico, usarlo
                nextTheme = newTheme;
            } else {
                // Ciclo entre temas: dark -> light -> custom -> dark
                if (prevTheme === 'dark') nextTheme = 'light';
                else if (prevTheme === 'light') nextTheme = 'custom';
                else nextTheme = 'dark';
            }

            localStorage.setItem('theme', nextTheme);
            return nextTheme;
        });
    };

    //Actualitzar color específic del tema
    const UpdateCustomThemeColor = (colorName: string, colorValue: string): void => {
        const customTheme = JSON.parse(localStorage.getItem('custom-theme') || '{}');
        customTheme[colorName] = colorValue
        localStorage.setItem('custom-theme', JSON.stringify(customTheme))

        document.documentElement.style.setProperty(`--${colorName}`, colorValue)
    }

    const UpdateCustomThemeImage = (imageName: string, imageValue: string): void => {
        const normalizedImageValue = normalizeThemeImageSrc(imageValue);

        // Guardar en localStorage
        const customTheme = JSON.parse(localStorage.getItem('custom-theme') || '{}');
        const imageKey = `image-${imageName}`;
        customTheme[imageKey] = normalizedImageValue;
        localStorage.setItem('custom-theme', JSON.stringify(customTheme));

        // Actualizar estado local para mejor rendimiento
        setThemeImages(prev => ({
            ...prev,
            [imageName]: normalizedImageValue
        }));
    };

    // Función para obtener una imagen del tema
    const getCustomThemeImage = (imageName: string): string => {
        // Primero intentamos obtener del estado local para mejor rendimiento
        if (themeImages[imageName]) {
            return normalizeThemeImageSrc(themeImages[imageName]);
        }

        // Si no está en el estado, intentamos obtenerla del localStorage
        try {
            const customTheme = JSON.parse(localStorage.getItem('custom-theme') || '{}');
            const imageKey = `image-${imageName}`;
            return normalizeThemeImageSrc(customTheme[imageKey] || '');
        } catch (error) {
            console.error('Error obteniendo imagen personalizada:', error);
            return '';
        }
    };

    //Aplicar colors personalitzats guardats al scss
    const applyCustomTheme = () => {
        const customTheme = JSON.parse(localStorage.getItem('custom-theme') || '{}');
        
        // Cargar imágenes en el estado
        const loadedImages: Record<string, string> = {};
        
        // Aplicar cada propiedad del tema
        Object.entries(customTheme).forEach(([key, value]) => {
            if (typeof value === 'string') {
                if (key.startsWith('image-')) {
                    // Es una imagen, la guardamos en el estado
                    const imageName = key.replace('image-', '');
                    loadedImages[imageName] = normalizeThemeImageSrc(value as string);
                } else {
                    // Es un color o propiedad CSS, la aplicamos
                    document.documentElement.style.setProperty(`--${key}`, value as string);
                }
            }
        });
        
        // Actualizar estado de imágenes
        setThemeImages(loadedImages);
    };

    // Cambiar tema a valors per defecte
    const resetCustomTheme = (): void => {
        localStorage.removeItem('custom-theme');
        applyCustomTheme();
        setThemeImages({});
        clearCustomProperties();
    };

    //Netejar propietats css per no sobrescriure les de light i dark
    const clearCustomProperties = () => {
        const customTheme = JSON.parse(localStorage.getItem('custom-theme') || '{}');
        Object.keys(customTheme).forEach(name => {
            if (!name.startsWith('image-')) {
                document.documentElement.style.removeProperty(`--${name}`);
            }
        });
    };

    useEffect(() => {
        document.documentElement.setAttribute('data-bs-theme', theme);

        if (theme === 'custom') {
            applyCustomTheme();
        } else {
            clearCustomProperties();
        }
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, UpdateCustomThemeColor, UpdateCustomThemeImage, getCustomThemeImage,resetCustomTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};